import { Injectable, NgZone } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarRef } from '@angular/material/snack-bar';

import { SnackbarData, SnackBarComponentType, SnackbarType } from '../models/snackbar';
import { SnackbarComponent } from './snackbar.component';

const validTypes: Array<string> = ['success', 'info', 'warning', 'error'];
const defaultType: string = validTypes[0];

const infoTypes: Array<string> = ['', 'log', 'default', 'blue'];
const successTypes: Array<string> = ['green', 'ok', 'good'];
const warningTypes: Array<string> = ['warn', 'yellow'];
const errorTypes: Array<string> = ['danger', 'fatal', 'red', 'bad'];

/**
 * Service to create/dismiss the snackbar
 *
 * @export
 * @class SnackBar
 */
@Injectable()
export class SnackBar {
  defaultConfig: MatSnackBarConfig;

  defaultDuration = {
    info: 5000,
    success: 2000,
    warning: 10000,
    error: -1
  };

  /**
   * Creates an instance of SnackBar.
   *
   * @param {MatSnackBar} snackBar
   * @memberof SnackBar
   */
  constructor(private snackBar: MatSnackBar, private zone: NgZone) {
    // this.addDefaultContainer();
    this.defaultConfig = new MatSnackBarConfig();
    this.defaultConfig = {
      horizontalPosition: 'right',
      duration: 10000,
      verticalPosition: 'top'
    };
  }

  /**
   * Returns the alert type
   *
   * @private
   * @param {string} type
   * @returns {string}
   * @memberof SnackBar
   */
  private getAlertType(type: string): string {
    type = type || defaultType;
    type = type.toLowerCase();

    if (validTypes.indexOf(type) !== -1) {
      return type;
    } else if (infoTypes.indexOf(type) !== -1) {
      return 'info';
    } else if (successTypes.indexOf(type) !== -1) {
      return 'success';
    } else if (warningTypes.indexOf(type) !== -1) {
      return 'warning';
    } else if (errorTypes.indexOf(type) !== -1) {
      return 'error';
    }
    return defaultType;
  }

  /**
   * Dismisses/Closes the notification if currently opened
   *
   * @memberof SnackBar
   */
  dismiss(): void {
    this.snackBar.dismiss();
  }

  /**
   * Uses the Material Snackbar service to create a default/custom notification
   *
   * @param {SnackbarData} snackbarData
   * @returns {MatSnackBarRef<SnackBarComponentType>}
   * @memberof SnackBar
   */
  addSnackBar(snackbarData: SnackbarData): MatSnackBarRef<SnackBarComponentType> {
    let newConfig: MatSnackBarConfig = snackbarData.configs;
    if (!newConfig) {
      newConfig = this.defaultConfig;
    }

    let durationMs = snackbarData.durationMs;
    let type = validTypes[snackbarData.type];
    if (type) {
      type = this.getAlertType(type);
      if (isNaN(durationMs) || durationMs < -1) {
        durationMs = this.defaultDuration[type];
      }
    }
    durationMs = durationMs ? durationMs : this.defaultConfig.duration;
    snackbarData.inputs = {
      ...snackbarData.inputs,
      type: snackbarData.componentClass ? SnackbarType.CUSTOM : SnackbarType.DEFAULT
    };
    newConfig = { ...newConfig, data: snackbarData.inputs, duration: durationMs, panelClass: type };

    const compClass = snackbarData.componentClass ? snackbarData.componentClass : SnackbarComponent;
    return this.zone.run(() => this.snackBar.openFromComponent(compClass, newConfig));
  }
}
